import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Compass, Play, Users, BookOpen, Brain, Sparkles, ChevronRight } from 'lucide-react';
import { VideoModal, VideoCard } from '@/components/VideoModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VRCampusPage() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/videos?page=vr-campus`)
      .then(res => setVideos(res.data))
      .catch(console.error);
  }, []);

  const features = [
    {
      icon: Compass,
      title: "Immersive Environments",
      description: "Explore richly recreated ancient civilizations including Olmec, Mayan, and Kemet (Ancient Egyptian) environments."
    },
    {
      icon: BookOpen,
      title: "Interactive Classrooms",
      description: "Attend live lectures, workshops, and hands-on learning experiences in our virtual classroom spaces."
    },
    {
      icon: Users,
      title: "Global Community",
      description: "Connect with educators and learners from around the world in our international networking hubs."
    },
    {
      icon: Brain,
      title: "Neurodiverse-Friendly",
      description: "Designed with accessibility in mind, supporting diverse learning styles and needs."
    },
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="vr-campus-page">
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 animated-gradient opacity-20" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Virtual Reality Experience</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-testid="vr-campus-title">
              Virtual Reality Campus
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Step into the future of education with our immersive virtual reality campus. Experience history, science, and culture in ways never before possible.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/vr-chat-meta-quest" className="btn-primary inline-flex items-center gap-2" data-testid="enter-vr-chat">
                <Play className="w-5 h-5" />
                Enter VR Chat World
              </Link>
              <Link to="/vr-demo-videos" className="btn-secondary inline-flex items-center gap-2" data-testid="watch-demos">
                Watch Demo Videos
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Preview Image */}
      <section className="py-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden glass-light"
          >
            <img
              src="https://static.prod-images.emergentagent.com/jobs/6a4505e5-3f70-4447-9214-c7fe0572ffa6/images/6a0f92298b626608a0fb990af56985ffdec330a4d04d8c70ecf0b397b11124a7.png"
              alt="Students wearing Meta Quest VR headsets in classroom"
              className="w-full h-[400px] object-cover"
              data-testid="vr-campus-image"
            />
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12"
          >
            Campus Features
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-xl glass-light card-hover"
                data-testid={`feature-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Section */}
      {videos.length > 0 && (
        <section className="py-16">
          <div className="container-custom">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12"
            >
              Campus Videos
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video, index) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  onClick={setSelectedVideo}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Ready to Explore?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join our virtual reality campus and experience education in a whole new dimension.
            </p>
            <Link to="/vr-chat-meta-quest" className="btn-primary inline-flex items-center gap-2">
              Enter VR Chat World
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <VideoModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoId={selectedVideo?.youtube_id}
        videoUrl={selectedVideo?.youtube_url}
        title={selectedVideo?.title}
      />
    </div>
  );
}
